import {
  HStack,
  IconButton,
  IconButtonProps,
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftElement,
  InputProps,
  StackProps,
  Text,
  TextProps,
  Tooltip,
  useClipboard,
} from "@chakra-ui/react";

import { IcoCopyToClipboard } from "@/common/components/Icons";

type CopyInputProps = StackProps & {
  value?: string;
  buttonLabel?: string;
  leftIcon?: React.ReactElement;
  variant?: InputProps["variant"];
  showValue?: boolean;
  _input?: Omit<InputProps, "value" | "isReadOnly" | "onClick">;
  _inputGroup?: InputGroupProps;
  _button?: Omit<IconButtonProps, "aria-label" | "icon" | "onClick">;
};

export const CopyInput = ({
  value = "",
  buttonLabel = "Copy to clipboard",
  leftIcon,
  variant = "ghost",
  showValue = true,
  _input,
  _inputGroup,
  _button,
  ...rest
}: CopyInputProps) => {
  const { hasCopied, onCopy } = useClipboard(value);

  return (
    <HStack {...rest}>
      {showValue && (
        <InputGroup {..._inputGroup}>
          {leftIcon && (
            <InputLeftElement color="dimmed" pointerEvents="none">
              {leftIcon}
            </InputLeftElement>
          )}
          <Input
            bg={variant === "ghost" ? "transparent" : undefined}
            borderRadius="md"
            data-testid="copy-input-value"
            overflow="hidden"
            px={variant === "ghost" ? 0 : undefined}
            size="sm"
            sx={
              variant === "ghost"
                ? {
                    "&:focus-visible": {
                      boxShadow: "none !important",
                    },
                  }
                : {
                    "&:focus-visible": {
                      borderColor: `inherit !important`,
                    },
                  }
            }
            textOverflow="ellipsis"
            value={value}
            variant={variant}
            whiteSpace="nowrap"
            isReadOnly
            onClick={(e) => {
              e.currentTarget.setSelectionRange(
                0,
                e.currentTarget.value.length,
              );
            }}
            {..._input}
          />
        </InputGroup>
      )}
      <Tooltip closeOnClick={false} label={hasCopied ? "Copied!" : buttonLabel}>
        <IconButton
          aria-label={buttonLabel}
          borderRadius="md"
          color="dimmed"
          icon={<IcoCopyToClipboard size="sm" />}
          p={0}
          size="xs"
          variant="ghost"
          onClick={onCopy}
          {..._button}
        />
      </Tooltip>
    </HStack>
  );
};

type CopyTextProps = StackProps & {
  value?: string;
  buttonLabel?: string;
  _text?: TextProps;
  _button?: Omit<IconButtonProps, "aria-label" | "icon" | "onClick">;
};

export const CopyText = ({
  value = "",
  buttonLabel = "Copy to clipboard",
  _text,
  _button,
  ...rest
}: CopyTextProps) => {
  const { hasCopied, onCopy } = useClipboard(value);

  return (
    <HStack {...rest}>
      <Text textOverflow="ellipsis" whiteSpace="nowrap" {..._text}>
        {value}
      </Text>
      <Tooltip closeOnClick={false} label={hasCopied ? "Copied!" : buttonLabel}>
        <IconButton
          aria-label={buttonLabel}
          borderRadius="md"
          color="dimmed"
          icon={<IcoCopyToClipboard size="sm" />}
          p={0}
          size="xs"
          variant="ghost"
          onClick={onCopy}
          {..._button}
        />
      </Tooltip>
    </HStack>
  );
};
